import { useGetSimpleUsersByRolesQuery } from 'store/services/core/user';
import { memo, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import { NameArray } from 'components/common/Table/cellComponents/NameArray';
import { Username } from 'components/common/Table/cellComponents/Username';
import { useTranslation } from 'react-i18next';

import { isArray } from 'utils/typeChecks';
import { styled, Typography } from '@mui/material';
import { useModals } from 'utils/hooks';
import { UserGroupsModal } from '../modals/UserGroupsModal';
import type { FormItemUiDefinition } from '../../../../types/formItem.ts';
import { MobileButton } from 'components/mui/common/MobileButton.tsx';

const UsersItemWrapper = styled('div')(() => ({
  width: '100%',
}));

export interface UsersItemProps {
  onConfirm?: () => void;
  formOptionShouldDirty?: boolean;
  formItemUiDefinition?: FormItemUiDefinition;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export const UsersItem = memo(({ onConfirm, formOptionShouldDirty, formItemUiDefinition, ...formItem }: UsersItemProps) => {
  const { t } = useTranslation();
  const { isMultiSelect, roleIds } = formItem.definition.users;
  const defaultValueRef = useRef(formItem?.defaultValue);
  const [modals, openModal, closeModal] = useModals({
    user_groups: false,
  });

  const { setValue, watch, trigger: triggerForm } = useFormContext();

  const { data: { items: users = [] } = {}, isFetching } = useGetSimpleUsersByRolesQuery(
    {
      page: 1,
      pageSize: null,
      orderBy: 'fullName',
      orderDesc: false,
      search: '',
      ids: roleIds,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const selectValue = isArray(watch(formItem.id));

  const selectedOptions = users
    ?.filter(({ id }) => selectValue.some((userId: string) => userId === id))
    .map(({ id, fullName }: { id: string; fullName: string }) => ({
      label: fullName || '',
      value: id,
    }));

  useEffect(() => {
    if (!defaultValueRef.current && formItem.defaultValue) {
      defaultValueRef.current = formItem.defaultValue;
    }
  }, [formItem.defaultValue]);

  const handleClose = () => {
    closeModal('user_groups')();
  };

  const handleSubmitModal = (selectedUsers: string[]) => {
    setValue(formItem.id, selectedUsers, { shouldDirty: Boolean(formOptionShouldDirty) });
    triggerForm(formItem.id);
    closeModal('user_groups')();
    defaultValueRef.current = null;
    onConfirm?.();
  };

  if (isFetching) return null;

  return (
    <>
      <UsersItemWrapper>
        {formItem?.detail ? (
          <NameArray
            value={(selectedOptions ?? []).map(({ label }: { label?: string }) => label ?? '')}
            formItemUiDefinition={formItemUiDefinition}
          />
        ) : (
          <MobileButton type="button" variant="outlined" onClick={openModal('user_groups')} fullWidth>
            {selectedOptions.length === 0 ? (
              t('common.select')
            ) : isMultiSelect ? (
              <NameArray value={(selectedOptions ?? []).map(({ label }: { label?: string }) => label ?? '')} />
            ) : (
              <Username value={selectedOptions[0]?.label ?? ''} />
            )}
          </MobileButton>
        )}

        {formItem.error && (
          <Typography variant="body1" color="error">
            {formItem.error.message}
          </Typography>
        )}
      </UsersItemWrapper>
      <UserGroupsModal
        title={t('common.select')}
        open={modals?.user_groups}
        isMultiSelect={isMultiSelect}
        roleIds={roleIds}
        selectedOptions={selectedOptions.map(({ value }: { value?: string }) => value ?? '')}
        onClose={handleClose}
        onChange={(selectedUsers: string[]) => setValue(formItem.id, selectedUsers)}
        formItem={formItem}
        onSubmit={handleSubmitModal}
      />
    </>
  );
});
